import React, { useMemo } from "react";
import { Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  BarController,
  LineController,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData,
} from "chart.js";

// Registro único (barras por dia + linha de referência do padrão). Sem dependências novas.
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  BarController,
  LineController,
  Tooltip,
  Legend,
  Filler,
);

export interface PreparationChartColors {
  bars: string[];
  ref: string;
  text: string;
  grid: string;
}

export interface PreparationChartProps {
  labels: string[];
  /** Tempo médio de ciclo por dia, em minutos; null em dia sem ciclos (vão na série). */
  values: Array<number | null>;
  /** Nº de ciclos por dia (para o tooltip). */
  counts: number[];
  /** Ciclo padrão em minutos (linha de referência tracejada). */
  standard: number;
  colors: PreparationChartColors;
  avgLabel: string;
  countLabel: string;
  diffLabel: string;
  standardLabel: string;
  unitMin: string;
  locale: string;
  height?: number;
}

const PreparationChart: React.FC<PreparationChartProps> = ({
  labels,
  values,
  counts,
  standard,
  colors,
  avgLabel,
  countLabel,
  diffLabel,
  standardLabel,
  unitMin,
  locale,
  height = 340,
}) => {
  const fmt = (v: number, dec = 1): string =>
    Number(v).toLocaleString(locale, { minimumFractionDigits: dec, maximumFractionDigits: dec });

  const data = useMemo<ChartData>(
    () => ({
      labels,
      datasets: [
        {
          type: "bar" as const,
          label: avgLabel,
          data: values as number[],
          backgroundColor: colors.bars,
          borderRadius: 4,
          maxBarThickness: 44,
          categoryPercentage: 0.72,
          barPercentage: 0.9,
          order: 2,
        },
        {
          type: "line" as const,
          label: standardLabel,
          data: labels.map(() => standard),
          borderColor: colors.ref,
          borderWidth: 2,
          borderDash: [6, 5],
          pointRadius: 0,
          pointHitRadius: 0,
          fill: false,
          tension: 0,
          order: 1,
        },
      ],
    }),
    [labels, values, standard, colors, avgLabel, standardLabel],
  );

  const options = useMemo<ChartOptions>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (item: any) => {
              if (item.dataset.type === "line") return `${standardLabel}: ${fmt(Number(item.raw))} ${unitMin}`;
              const raw = item.raw;
              if (raw == null) return "";
              const i = item.dataIndex;
              const diff = Number(raw) - standard;
              const sign = diff > 0 ? "+" : "";
              return [
                `${avgLabel}: ${fmt(Number(raw))} ${unitMin}`,
                `${countLabel}: ${counts[i] ?? 0}`,
                `${diffLabel}: ${sign}${fmt(diff)} ${unitMin}`,
              ];
            },
          },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: colors.text, font: { size: 11 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 15 } },
        y: {
          beginAtZero: true,
          grid: { color: colors.grid },
          ticks: { color: colors.text, font: { size: 11 }, callback: (v: any) => `${v} ${unitMin}` },
        },
      },
    }),
    [colors, standard, counts, avgLabel, countLabel, diffLabel, standardLabel, unitMin, locale],
  );

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <Chart type="bar" data={data} options={options} />
    </div>
  );
};

export default PreparationChart;
